import React, { useEffect, useRef } from 'react';
import { Animated, Pressable, Text, View, StyleSheet } from 'react-native';
import { Feather } from '@expo/vector-icons';
import { Paprika } from '../../../styles/theme';
import { DietDot } from '../../weekly/v2/primitives/DietDot';
import { TimeChip } from '../../weekly/v2/primitives/TimeChip';
import type { DietaryRole } from '../../../redux/mealPlanSlice';
import { useTranslation } from 'react-i18next';
import { RemoteImage } from '../../shared/RemoteImage';

interface Props {
  title: string;
  image?: string | null;
  portions: number;
  minutes?: number | null;
  dietaryRole?: DietaryRole;
  shown: boolean;
  onShuffle: () => void;
  onPortionsTap: () => void;
}

export function SuggCardM({ title, image, portions, minutes, dietaryRole, shown, onShuffle, onPortionsTap }: Props) {
  const { t } = useTranslation();
  const opacity = useRef(new Animated.Value(0)).current;
  const translateY = useRef(new Animated.Value(12)).current;

  useEffect(() => {
    if (shown) {
      Animated.parallel([
        Animated.timing(opacity, { toValue: 1, duration: 440, useNativeDriver: true }),
        Animated.timing(translateY, { toValue: 0, duration: 440, useNativeDriver: true }),
      ]).start();
    }
  }, [shown]);

  return (
    <Animated.View style={[styles.card, { opacity, transform: [{ translateY }] }]}>
      {/* Photo */}
      <View style={styles.photoWrap}>
        <RemoteImage
          uri={image}
          variant="thumb"
          style={styles.photo}
          resizeMode="cover"
          fallback={<View style={[styles.photo, styles.photoPlaceholder]} />}
        />

        {/* DietDot pill top-left */}
        {dietaryRole && (
          <View style={styles.dietPill}>
            <DietDot role={dietaryRole} size={8} />
          </View>
        )}

        {/* Portions badge top-right */}
        <Pressable style={styles.portionsBadge} onPress={onPortionsTap}>
          <Text style={styles.portionsText}>{portions}</Text>
        </Pressable>

        {/* Shuffle button bottom-right */}
        <Pressable style={styles.shuffleBtn} onPress={onShuffle}>
          <Feather name="refresh-cw" size={11} color={Paprika.textSecondary} />
          <Text style={styles.shuffleBtnText}>{t('mealConfig.anotherRecipe')}</Text>
        </Pressable>
      </View>

      {/* Title + time row */}
      <View style={styles.footer}>
        <Text style={styles.title} numberOfLines={2}>{title}</Text>
        {minutes != null && <TimeChip minutes={minutes} />}
      </View>
    </Animated.View>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 10,
    overflow: 'hidden',
    backgroundColor: Paprika.surfaceLowest,
    marginBottom: 8,
    shadowColor: 'rgba(82, 68, 60, 0.08)',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 1,
    shadowRadius: 8,
    elevation: 2,
  },
  photoWrap: {
    height: 150,
    position: 'relative',
    backgroundColor: Paprika.surfaceLow,
  },
  photo: {
    width: '100%' as any,
    height: '100%' as any,
  },
  photoPlaceholder: {
    backgroundColor: Paprika.bgSunk,
  },
  dietPill: {
    position: 'absolute',
    top: 10,
    left: 10,
    width: 26,
    height: 26,
    borderRadius: 13,
    backgroundColor: 'rgba(255,255,255,0.92)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  portionsBadge: {
    position: 'absolute',
    top: 10,
    right: 10,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 12,
    paddingHorizontal: 8,
    paddingVertical: 3,
    minWidth: 28,
    alignItems: 'center',
  },
  portionsText: {
    fontFamily: 'Newsreader-Regular',
    fontSize: 13,
    color: Paprika.textPrimary,
  },
  shuffleBtn: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    backgroundColor: 'rgba(255,255,255,0.92)',
    borderRadius: 100,
    paddingHorizontal: 10,
    paddingVertical: 5,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  shuffleBtnText: {
    fontFamily: 'Inter-Medium',
    fontSize: 11,
    color: Paprika.textSecondary,
  },
  footer: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  title: {
    flex: 1,
    fontFamily: 'Newsreader-Italic',
    fontSize: 14,
    lineHeight: 18,
    color: Paprika.textPrimary,
  },
});
